import React, { useState, useEffect, useMemo } from "react";
import {
  getAdminBookings,
  getAllProviders,
  getProviderDetail,
  updateBookingProvider,
  updateBookingConfirmation,
} from "./adminService";

import { ProviderModal } from "./SubComponent/ProviderModal";
import ConfirmModal from "./SubComponent/ConfirmModal";
import "./ManagerScreen.css";

const STATUS_FILTERS = [
  { id: "all", label: "All", icon: "bi-grid" },
  { id: "pending", label: "Pending", icon: "bi-hourglass-split" },
  { id: "confirmed", label: "Confirmed", icon: "bi-check2-circle" },
  { id: "declined", label: "Declined", icon: "bi-x-circle" },
  { id: "paid", label: "Paid", icon: "bi-cash-coin" },
];

const ManagerScreen = ({ globalSearch = "" }) => {
  const token = localStorage.getItem("@uphealth_login_token");

  const [allProviders, setAllProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    pageSize: 15,
    totalItems: 0,
    totalPages: 1,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  const [providerModal, setProviderModal] = useState({
    open: false,
    booking: null,
  });

  const openProviderModal = (booking) =>
    setProviderModal({ open: true, booking });
  const closeProviderModal = () =>
    setProviderModal({ open: false, booking: null });

  const [confirmBooking, setConfirmBooking] = useState(null);

  const requestConfirmBooking = (booking) => setConfirmBooking(booking);
  const cancelConfirmBooking = () => setConfirmBooking(null);
  const executeConfirmBooking = () => {
    if (confirmBooking) handleConfirmationSubmit(confirmBooking.booking_id, true);
    setConfirmBooking(null);
  };

  /* ── Data fetching ── */
  const fetchData = async (page = 1, search = "") => {
    setLoading(true);
    setError(null);
    try {
      if (allProviders.length === 0) {
        const providersRes = await getAllProviders(token);
        setAllProviders(providersRes.data);
      }
      const bookingsRes = await getAdminBookings(
        token,
        page,
        paginationInfo.pageSize,
        search
      );

      if (bookingsRes.data && bookingsRes.data.pagination) {
        setAllBookings(bookingsRes.data.data);
        setPaginationInfo(bookingsRes.data.pagination);
      } else if (Array.isArray(bookingsRes.data)) {
        setAllBookings(bookingsRes.data);
        setPaginationInfo({
          currentPage: 1, pageSize: 15,
          totalItems: bookingsRes.data.length, totalPages: 1,
        });
      } else {
        throw new Error("Unexpected data format from API.");
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Apply global header search if local search is empty
  useEffect(() => {
    const effectiveSearch = debouncedSearchTerm || globalSearch;
    fetchData(1, effectiveSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, debouncedSearchTerm, globalSearch]);

  /* ── Action handlers ── */
  const handleProviderChange = async (booking_id, new_provider_id) => {
    if (!new_provider_id) return;
    try {
      await updateBookingProvider(token, booking_id, new_provider_id);
      fetchData(paginationInfo.currentPage);
    } catch (err) {
      alert("Failed to update provider.");
    }
  };

  const handleConfirmationSubmit = async (booking_id, acceptanceStatus) => {
    try {
      await updateBookingConfirmation(token, booking_id, acceptanceStatus);
      fetchData(paginationInfo.currentPage);
    } catch (err) {
      alert(`Failed to ${acceptanceStatus ? "confirm" : "decline"} booking.`);
    }
  };

  const getStatus = (b) => {
    if (b.paid) return { id: "paid", label: "Paid", cls: "dt-pill--accent" };
    if (b.acceptance === true) return { id: "confirmed", label: "Confirmed", cls: "dt-pill--success" };
    if (b.acceptance === false) return { id: "declined", label: "Declined", cls: "dt-pill--neutral" };
    return { id: "pending", label: "Pending", cls: "dt-pill--warn" };
  };

  /* ── Counts per filter (current page) ── */
  const counts = useMemo(() => {
    const c = { all: allBookings.length, pending: 0, confirmed: 0, declined: 0, paid: 0 };
    allBookings.forEach((b) => { c[getStatus(b).id]++; });
    return c;
  }, [allBookings]);

  const filteredBookings = useMemo(() => {
    if (filter === "all") return allBookings;
    return allBookings.filter((b) => getStatus(b).id === filter);
  }, [allBookings, filter]);

  /* ── Pagination ── */
  const handleNextPage = () => {
    if (paginationInfo.currentPage < paginationInfo.totalPages) {
      fetchData(paginationInfo.currentPage + 1, debouncedSearchTerm || globalSearch);
    }
  };
  const handlePreviousPage = () => {
    if (paginationInfo.currentPage > 1) {
      fetchData(paginationInfo.currentPage - 1, debouncedSearchTerm || globalSearch);
    }
  };

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="manager-container">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Bookings</h1>
          <div className="page-header__subtitle">
            Showing <strong style={{ color: "var(--text-1)" }}>{filteredBookings.length}</strong> on this page · <strong style={{ color: "var(--text-1)" }}>{paginationInfo.totalItems}</strong> total bookings
          </div>
        </div>
        {/* <div className="page-header__right">
          <button className="ov-quick-btn">
            <i className="bi bi-download" /> Export
          </button>
          <button className="ov-quick-btn ov-quick-btn--accent">
            <i className="bi bi-plus-lg" /> New Booking
          </button>
        </div> */}
      </div>

      {/* ── Toolbar: Filters + Search ── */}
      <div className="dt-toolbar">
        <div className="dt-toolbar__left">
          <div className="dt-filters">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                className={`dt-filter-pill ${filter === f.id ? "dt-filter-pill--active" : ""}`}
                onClick={() => setFilter(f.id)}
              >
                <i className={`bi ${f.icon}`} />
                {f.label}
                <span className="dt-filter-pill__count">{counts[f.id] || 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="dt-toolbar__right">
          <div className="dt-search">
            <i className="bi bi-search dt-search__icon" />
            <input
              type="text"
              placeholder="Search by name, service, location…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Data Table ── */}
      <div className="dt-shell">
        {loading ? (
          <div className="dt-loading">
            <div className="spinner-border" />
            <span>Loading bookings…</span>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="dt-empty">
            <div className="dt-empty__icon"><i className="bi bi-calendar-x" /></div>
            <h4>No bookings found</h4>
            <p>{filter !== "all" ? "Try a different filter." : "Try a different search term."}</p>
          </div>
        ) : (
          <table className="dt-table">
            <thead>
              <tr>
                <th>Booking</th>
                <th>Service</th>
                <th>Location</th>
                <th>Provider</th>
                <th>Status</th>
                <th className="dt-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((b) => {
                const status = getStatus(b);
                const initials = `${(b.first_name?.[0] || "")}${(b.last_name?.[0] || "")}`.toUpperCase() || "U";
                const nearbyProviders = allProviders.filter(
                  (p) =>
                    p.verification === true &&
                    p.city?.toLowerCase() === b.city?.toLowerCase() &&
                    p.country?.toLowerCase() === b.country?.toLowerCase()
                );

                return (
                  <tr key={b.booking_id}>
                    <td>
                      <div className="dt-user">
                        <div className="dt-avatar">{initials}</div>
                        <div className="dt-cell-stack">
                          <div className="dt-name">{b.first_name} {b.last_name}</div>
                          <div className="dt-mono">#{String(b.booking_id).slice(-8)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="dt-name" style={{ fontSize: "0.84rem" }}>{b.care_service_name || "—"}</div>
                    </td>
                    <td>
                      <div className="dt-cell-stack">
                        <div className="dt-name" style={{ fontSize: "0.83rem" }}>
                          <i className="bi bi-geo-alt" style={{ color: "var(--text-3)", marginRight: "4px", fontSize: "0.78rem" }} />
                          {b.city}
                        </div>
                        <div className="dt-sub">{b.country}</div>
                      </div>
                    </td>
                    <td>
                      {nearbyProviders.length > 0 ? (
                        (() => {
                          const current = nearbyProviders.find(
                            (p) => p.provider_id === b.provider_id
                          );
                          return (
                            <button
                              type="button"
                              className={`provider-trigger ${current ? "provider-trigger--assigned" : "provider-trigger--empty"
                                }`}
                              onClick={() =>
                                openProviderModal({
                                  booking_id: b.booking_id,
                                  provider_id: b.provider_id,
                                  providers: nearbyProviders,
                                  label: `#${String(b.booking_id).slice(-8)}`,
                                })
                              }
                              title={current ? "Change provider" : "Assign provider"}
                            >
                              {current ? (
                                <>
                                  <span className="provider-trigger__avatar">
                                    {current.img_url ? (
                                      <img src={current.img_url} alt="" />
                                    ) : (
                                      <i className="bi bi-person-fill" />
                                    )}
                                  </span>
                                  <span className="provider-trigger__text">
                                    <span className="provider-trigger__name">
                                      {current.provider_name}
                                    </span>
                                    <span className="provider-trigger__sub">
                                      {current.title || "Tap to change"}
                                    </span>
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="provider-trigger__avatar provider-trigger__avatar--empty">
                                    <i className="bi bi-person-plus" />
                                  </span>
                                  <span className="provider-trigger__text">
                                    <span className="provider-trigger__name">Assign provider</span>
                                    <span className="provider-trigger__sub">
                                      {nearbyProviders.length} nearby
                                    </span>
                                  </span>
                                </>
                              )}
                              <i className="bi bi-chevron-right provider-trigger__chev" />
                            </button>
                          );
                        })()
                      ) : (
                        <span className="dt-pill dt-pill--neutral">
                          <span style={{ width: 0 }} /> No nearby
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`dt-pill ${status.cls}`}>{status.label}</span>
                    </td>
                    <td className="dt-td--right">
                      <div className="manager-actions">
                        {status.id === "pending" && (
                          <button
                            className="dt-action-btn dt-action-btn--accent"
                            title="Confirm booking"
                            onClick={() => requestConfirmBooking(b)}
                          >
                            <i className="bi bi-check-lg" />
                          </button>
                        )}
                        {status.id === "pending" && (
                          <button
                            className="dt-action-btn dt-action-btn--danger"
                            title="Decline booking"
                            onClick={() => handleConfirmationSubmit(b.booking_id, false)}
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        )}
                        <div className="dropdown" style={{ display: "inline-block" }}>
                          <button
                            className="dt-action-btn"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title="More actions"
                          >
                            <i className="bi bi-three-dots" />
                          </button>
                          <ul className="dropdown-menu action-menu-dark dropdown-menu-end">
                            <li>
                              <button className="dropdown-item" onClick={() => alert("Contact patient — coming soon")}>
                                <i className="bi bi-envelope me-2" /> Contact Patient
                              </button>
                            </li>
                            <li>
                              <button className="dropdown-item" onClick={() => alert("Edit booking — coming soon")}>
                                <i className="bi bi-pencil-square me-2" /> Edit Booking
                              </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                              <button className="dropdown-item" onClick={() => alert("View details — coming soon")}>
                                <i className="bi bi-eye me-2" /> View Details
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* ── Pagination Footer ── */}
        {!loading && filteredBookings.length > 0 && (
          <div className="dt-footer">
            <div>
              Page <span className="dt-pager-info">{paginationInfo.currentPage}</span> of {paginationInfo.totalPages}
              <span style={{ margin: "0 10px", color: "var(--border-3)" }}>·</span>
              <span className="dt-pager-info">{paginationInfo.totalItems}</span> total
            </div>
            <div className="dt-footer__pager">
              <button
                className="dt-pager-btn"
                onClick={handlePreviousPage}
                disabled={paginationInfo.currentPage <= 1 || loading}
              >
                <i className="bi bi-chevron-left" />
              </button>
              <button
                className="dt-pager-btn"
                onClick={handleNextPage}
                disabled={paginationInfo.currentPage >= paginationInfo.totalPages || loading}
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>

      <ProviderModal
        show={providerModal.open}
        onClose={closeProviderModal}
        providers={providerModal.booking?.providers || []}
        selectedProviderId={providerModal.booking?.provider_id}
        bookingLabel={providerModal.booking?.label}
        onSelect={(newProviderId) =>
          handleProviderChange(providerModal.booking?.booking_id, newProviderId)
        }
        onFetchDetail={(providerId) => getProviderDetail(token, providerId)}
      />

      <ConfirmModal
        show={!!confirmBooking}
        onConfirm={executeConfirmBooking}
        onCancel={cancelConfirmBooking}
        title="Confirm booking?"
        message={
          confirmBooking
            ? `Confirm booking for ${confirmBooking.first_name} ${confirmBooking.last_name}?`
            : undefined
        }
        confirmLabel="Confirm"
        variant="accent"
      />
    </div>
  );
};

export default ManagerScreen;
